import type { Currency } from '@repo-level/core-front';
import AudFlag from 'assets/currency/AUD.svg';
import BchFlag from 'assets/currency/BCH.svg';
import BrlFlag from 'assets/currency/BRL.svg';
import BtcFlag from 'assets/currency/BTC.svg';
import CadFlag from 'assets/currency/CAD.svg';
import DogFlag from 'assets/currency/DOG.svg';
import EthFlag from 'assets/currency/ETH.svg';
import EurFlag from 'assets/currency/EUR.svg';
import JpyFlag from 'assets/currency/JPY.svg';
import LtcFlag from 'assets/currency/LTC.svg';
import NokFlag from 'assets/currency/NOK.svg';
import NzdFlag from 'assets/currency/NZD.svg';
import PlnFlag from 'assets/currency/PLN.svg';
import RubFlag from 'assets/currency/RUB.svg';
import UsdFlag from 'assets/currency/USD.svg';
import UsdtFlag from 'assets/currency/USDT.svg';
import { Maybe } from 'monet';
import * as R from 'ramda';
import { MAP_CURRENCY_RESTRICTIONS } from './conf';

export enum CurrencyCode {
  EUR = 'EUR',
  USD = 'USD',
  CAD = 'CAD',
  AUD = 'AUD',
  NOK = 'NOK',
  RUB = 'RUB',
  PLN = 'PLN',
  NZD = 'NZD',
  JPY = 'JPY',
  BRL = 'BRL',
  // CRYPTO
  BTC = 'BTC',
  MBTC = 'mBTC',
  BCH = 'BCH',
  ETH = 'ETH',
  LTC = 'LTC',
  DOG = 'DOG',
  USDT = 'USDT',
  USDC = 'USDC',
}

export const mapCurrencyFullName: { [currencyCode: string]: string } = {
  [CurrencyCode.EUR]: 'Euro',
  [CurrencyCode.USD]: 'United States Dollar',
  [CurrencyCode.CAD]: 'Canadian Dollar',
  [CurrencyCode.AUD]: 'Australian Dollar',
  [CurrencyCode.NOK]: 'Norwegian Krone',
  [CurrencyCode.RUB]: 'Russian Ruble',
  [CurrencyCode.PLN]: 'Polish Złoty',
  [CurrencyCode.NZD]: 'New Zealand Dollar',
  [CurrencyCode.JPY]: 'Japanese Yen',
  [CurrencyCode.BRL]: 'Brazilian Real',
  [CurrencyCode.BTC]: 'Bitcoin',
  [CurrencyCode.BCH]: 'Bitcoin Cash',
  [CurrencyCode.ETH]: 'Ethereum',
  [CurrencyCode.LTC]: 'Litecoin',
  [CurrencyCode.DOG]: 'Dogecoin',
  [CurrencyCode.USDT]: 'Tether',
};

export const mapLocaleCurrencySymbol: { [locale: string]: string } = {
  en: '€/${}',
  de: '€{}',
  'en-CA': 'C${}',
  'fr-CA': 'C${}',
  it: '€{}',
  nn: '{} kr',
  pt: '€{}',
  'pt-BR': 'R${}',
  'en-AU': 'A${}',
  'en-IE': '€{}',
  'en-NZ': 'NZ${}',
  ir: '€{}',
  fr: '€{}',
};

export const mapCurrencySymbol: {
  [currency: string]: {
    symbol: string;
    beforeAmount?: boolean;
    icon: string;
    name?: string;
  };
} = {
  // fiat
  [CurrencyCode.EUR]: {
    symbol: '€',
    beforeAmount: true,
    icon: EurFlag,
    name: 'Euro',
  },
  [CurrencyCode.USD]: {
    symbol: '$',
    beforeAmount: true,
    icon: UsdFlag,
    name: 'United States Dollar',
  },
  [CurrencyCode.CAD]: {
    symbol: 'C$',
    beforeAmount: true,
    icon: CadFlag,
    name: 'Canadian Dollar',
  },
  [CurrencyCode.AUD]: {
    symbol: 'A$',
    beforeAmount: true,
    icon: AudFlag,
    name: 'Australian Dollar',
  },
  [CurrencyCode.NOK]: { symbol: ' kr', icon: NokFlag, name: 'Norwegian krone' },
  [CurrencyCode.RUB]: { symbol: ' ₽', icon: RubFlag, name: 'Russian Ruble' },
  [CurrencyCode.PLN]: {
    symbol: 'zł ',
    beforeAmount: true,
    icon: PlnFlag,
    name: 'Polish Złoty',
  },
  [CurrencyCode.NZD]: {
    symbol: 'NZ$',
    beforeAmount: true,
    icon: NzdFlag,
    name: 'New Zealand Dollar',
  },
  [CurrencyCode.JPY]: {
    symbol: '¥',
    beforeAmount: true,
    icon: JpyFlag,
    name: 'Japanese Yen',
  },
  [CurrencyCode.BRL]: {
    symbol: 'R$',
    beforeAmount: true,
    icon: BrlFlag,
    name: 'Brazilian Real',
  },

  // crypto
  [CurrencyCode.BTC]: {
    symbol: 'BTC',
    icon: BtcFlag,
    name: 'Bitcoin',
  },
  [CurrencyCode.BCH]: {
    symbol: 'BCH',
    icon: BchFlag,
    name: 'Bitcoin Cash',
  },
  [CurrencyCode.ETH]: {
    symbol: 'ETH',
    icon: EthFlag,
    name: 'Ethereum',
  },
  [CurrencyCode.LTC]: {
    symbol: 'LTC',
    icon: LtcFlag,
    name: 'Litecoin',
  },
  [CurrencyCode.DOG]: { symbol: ' DOGE', icon: DogFlag, name: 'Dogecoin' },
  [CurrencyCode.USDT]: { symbol: ' USDT', icon: UsdtFlag, name: 'Tether' },
};

export const mapCurrencyFlags: {
  [code: string]: string;
} = {
  [CurrencyCode.EUR]: EurFlag,
  [CurrencyCode.USD]: UsdFlag,
  [CurrencyCode.CAD]: CadFlag,
  [CurrencyCode.AUD]: AudFlag,
  [CurrencyCode.NOK]: NokFlag,
  [CurrencyCode.RUB]: RubFlag,
  [CurrencyCode.PLN]: PlnFlag,
  [CurrencyCode.NZD]: NzdFlag,
  [CurrencyCode.JPY]: JpyFlag,
  [CurrencyCode.BRL]: BrlFlag,
  [CurrencyCode.BTC]: BtcFlag,
  [CurrencyCode.BCH]: BchFlag,
  [CurrencyCode.ETH]: EthFlag,
  [CurrencyCode.LTC]: LtcFlag,
  [CurrencyCode.DOG]: DogFlag,
  [CurrencyCode.USDT]: UsdtFlag,
};

export const ALL_FIAT = [
  CurrencyCode.EUR,
  CurrencyCode.USD,
  CurrencyCode.CAD,
  CurrencyCode.AUD,
  CurrencyCode.NOK,
  CurrencyCode.RUB,
  CurrencyCode.PLN,
  CurrencyCode.NZD,
  CurrencyCode.JPY,
  CurrencyCode.BRL,
];

export const currencyNameMap: Record<string, string> = {
  [CurrencyCode.LTC]: 'mLTC',
  [CurrencyCode.DOG]: 'DOGE',
  [CurrencyCode.BTC]: 'mBTC',
  [CurrencyCode.BCH]: 'mBCH',
  [CurrencyCode.ETH]: 'mETH',
};

export const getCurrencyName = (currency: string) =>
  currencyNameMap[currency] ?? currency;

export const decimalPlacesMapCrypto: Record<string, number> = {
  mBTC: 4,
  mETH: 2,
  mLTC: 2,
  DOGE: 2,
  USDT: 2,
  mBCH: 3,
};

export const fixedAmount = (
  amount: number,
  currencyCode?: string,
  decimals?: number
) => {
  const decimalPlaces = (() => {
    if (decimals !== undefined) return decimals;
    if (currencyCode) return decimalPlacesMapCrypto[currencyCode];

    return 0;
  })();

  return amount ? amount.toFixed(decimalPlaces) : '0';
};

const symbolSpan = (symbol: string) => <span className="symbol">{symbol}</span>;

export const amountWithCurrencySymbol = R.curry(
  (currencyCode: string | undefined, amount: number | string) => {
    const decimals = decimalPlacesMapCrypto[currencyCode || ''] || 2;

    const formattedAmount =
      typeof amount === 'string'
        ? amount
        : fixedAmount(amount, currencyCode, decimals);

    if (!currencyCode) return formattedAmount;

    const currencyData = mapCurrencySymbol[currencyCode];

    if (!currencyData) {
      return (
        <>
          {formattedAmount} {symbolSpan(currencyCode)}
        </>
      );
    }

    const { symbol, beforeAmount } = currencyData;

    return (
      <>
        {beforeAmount && symbolSpan(symbol)}
        {formattedAmount}
        {!beforeAmount && symbolSpan(symbol)}
      </>
    );
  }
);

export const amountWithCurrencySymbolString = R.curry(
  (currencyCode: string | undefined, amount: number | string) => {
    if (currencyCode == null) return amount.toString();

    const currencyData = mapCurrencySymbol[currencyCode];

    if (!currencyData) return `${amount} ${currencyCode}`;

    const { symbol, beforeAmount } = currencyData;

    return beforeAmount ? `${symbol}${amount}` : `${amount}${symbol}`;
  }
);

export const formatCurrencyForTransl = (
  value: string | undefined,
  locale: string
) => {
  if (value == null) return;

  const match = value.match(/\{\{([0-9]+)\}\}/);
  const fullAmount = match?.[0];
  const amount = match?.[1];

  if (fullAmount == null || amount == null) return value;

  const amountWithCurrency = mapLocaleCurrencySymbol[locale]?.replace(
    '{}',
    amount
  );

  return value.replace(fullAmount, amountWithCurrency);
};

export const getEnabledCurrenciesByCountry = (
  currencies: Currency[],
  countryCode: string
) =>
  Maybe.fromEmpty(MAP_CURRENCY_RESTRICTIONS[countryCode])
    .map((unavailableCurrencies) =>
      currencies.filter(
        (currency) => !unavailableCurrencies.includes(currency.code)
      )
    )
    .getOrElse(currencies);

export const mapMinDepositEquivalents: { [currency: string]: number } = {
  [CurrencyCode.EUR]: 10,
  [CurrencyCode.USD]: 10,
  [CurrencyCode.CAD]: 10,
  [CurrencyCode.AUD]: 10,
  [CurrencyCode.NOK]: 100,
  [CurrencyCode.NZD]: 10,
  [CurrencyCode.BTC]: 0.35,
  [CurrencyCode.BCH]: 80,
  [CurrencyCode.ETH]: 5.5,
  [CurrencyCode.LTC]: 100,
  [CurrencyCode.DOG]: 150,
  [CurrencyCode.USDT]: 10,
};

export const mapDefaultDepositEquivalents: { [currency: string]: number } = {
  [CurrencyCode.EUR]: 20,
  [CurrencyCode.USD]: 20,
  [CurrencyCode.CAD]: 20,
  [CurrencyCode.AUD]: 20,
  [CurrencyCode.NOK]: 200,
  [CurrencyCode.NZD]: 20,
  [CurrencyCode.BTC]: 0.0005,
  [CurrencyCode.BCH]: 0.06,
  [CurrencyCode.ETH]: 0.007,
  [CurrencyCode.LTC]: 0.2,
  [CurrencyCode.DOG]: 150,
  [CurrencyCode.USDT]: 20,
};

export const CurrencyLimit: Record<string, number> = {
  [CurrencyCode.CAD]: 1000,
  [CurrencyCode.AUD]: 1000,
  [CurrencyCode.EUR]: 1000,
  [CurrencyCode.NZD]: 1000,
  [CurrencyCode.USD]: 1000,
  [CurrencyCode.NOK]: 10_000,
  [CurrencyCode.BRL]: 10_000,
  [CurrencyCode.BTC]: 20,
  [CurrencyCode.BCH]: 2250,
  [CurrencyCode.ETH]: 700,
  [CurrencyCode.LTC]: 6000,
  [CurrencyCode.DOG]: 4000,
  [CurrencyCode.USDT]: 1000,
};
